import {
  drawBlobBody,
  drawBlobEyes,
  drawBlobHat,
  drawBlobMobProcedural,
  drawBlobShadowRing,
  type BlobMobConfig,
} from './shared';

export function drawBlobMageProcedural(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  alpha: number,
  rotation: number = 0
): void {
  drawBlobMobProcedural(ctx, centerX, centerY, size, alpha, rotation, {
    bodyColor: '#5d4a68',
    faceColor: '#bca58a',
    shadowColor: 'rgba(67, 56, 89, 0.34)',
    rimColor: '#8f7452',
    accentColor: '#d7c6a1',
    accentColorSecondary: '#44344d',
    eyeColor: '#21140f',
    hatType: 'wizard',
    weaponType: 'staff',
    accessoryType: 'collar',
  });
}

export function drawBlobKnightProcedural(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  alpha: number,
  rotation: number = 0
): void {
  drawBlobMobProcedural(ctx, centerX, centerY, size, alpha, rotation, {
    bodyColor: '#6c4b49',
    faceColor: '#bda183',
    shadowColor: 'rgba(82, 68, 66, 0.34)',
    rimColor: '#92724d',
    accentColor: '#c7c9c3',
    accentColorSecondary: '#5c6066',
    eyeColor: '#22130e',
    hatType: 'helmet',
    weaponType: 'sword',
    accessoryType: 'shield',
  });
}

export function drawBlobArcherProcedural(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  alpha: number,
  rotation: number = 0
): void {
  drawBlobMobProcedural(ctx, centerX, centerY, size, alpha, rotation, {
    bodyColor: '#556244',
    faceColor: '#b79d7f',
    shadowColor: 'rgba(73, 90, 58, 0.34)',
    rimColor: '#8f744f',
    accentColor: '#b8c48e',
    accentColorSecondary: '#465137',
    eyeColor: '#24140e',
    hatType: 'hood',
    weaponType: 'bow',
    accessoryType: 'none',
  });
}

export function drawBlobClericProcedural(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  alpha: number,
  rotation: number = 0
): void {
  drawBlobMobProcedural(ctx, centerX, centerY, size, alpha, rotation, {
    bodyColor: '#8a8670',
    faceColor: '#bfa98a',
    shadowColor: 'rgba(109, 101, 74, 0.32)',
    rimColor: '#9a7b54',
    accentColor: '#d9d4c3',
    accentColorSecondary: '#6d5a37',
    eyeColor: '#23150f',
    hatType: 'hood',
    weaponType: 'mace',
    accessoryType: 'orb',
  });
}

export function drawBlobRogueProcedural(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  alpha: number,
  rotation: number = 0
): void {
  drawBlobMobProcedural(ctx, centerX, centerY, size, alpha, rotation, {
    bodyColor: '#474742',
    faceColor: '#ad9377',
    shadowColor: 'rgba(53, 53, 47, 0.34)',
    rimColor: '#7f7c73',
    accentColor: '#c9c7bf',
    accentColorSecondary: '#252521',
    eyeColor: '#170c09',
    hatType: 'hood',
    weaponType: 'dagger',
    accessoryType: 'none',
  });
}

export function drawBlobPaladinProcedural(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  alpha: number,
  rotation: number = 0
): void {
  drawBlobMobProcedural(ctx, centerX, centerY, size, alpha, rotation, {
    bodyColor: '#9c9a8e',
    faceColor: '#bea487',
    shadowColor: 'rgba(118, 114, 99, 0.32)',
    rimColor: '#9b7c56',
    accentColor: '#d9d2b5',
    accentColorSecondary: '#7d8083',
    eyeColor: '#21130e',
    hatType: 'helmet',
    weaponType: 'sword',
    accessoryType: 'shield',
  });
}

export function drawBlobWarlockProcedural(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  alpha: number,
  rotation: number = 0
): void {
  drawBlobMobProcedural(ctx, centerX, centerY, size, alpha, rotation, {
    bodyColor: '#584562',
    faceColor: '#af9277',
    shadowColor: 'rgba(74, 59, 82, 0.34)',
    rimColor: '#8e7097',
    accentColor: '#b897a8',
    accentColorSecondary: '#3b2d43',
    eyeColor: '#190d0d',
    hatType: 'wizard',
    weaponType: 'staff',
    accessoryType: 'book',
  });
}

export function drawBlobCrystalWizardProcedural(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  alpha: number,
  rotation: number = 0
): void {
  const config: BlobMobConfig = {
    bodyColor: '#5a4d88',
    faceColor: '#f3cdb9',
    shadowColor: 'rgba(61, 49, 103, 0.34)',
    rimColor: '#d7dce8',
    accentColor: '#8de6ff',
    accentColorSecondary: '#3d2a8a',
    eyeColor: '#1f1530',
    hatType: 'wizard',
    weaponType: 'none',
    accessoryType: 'none',
  };

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(centerX, centerY);
  ctx.rotate((rotation * Math.PI) / 180);

  ctx.shadowColor = config.shadowColor;
  ctx.shadowBlur = size * 0.17;
  drawBlobShadowRing(ctx, size, config.shadowColor);
  drawBlobBody(ctx, size, config);
  ctx.shadowBlur = 0;
  drawBlobHat(ctx, size, config);

  ctx.beginPath();
  ctx.moveTo(-size * 0.08, -size * 0.005);
  ctx.lineTo(0, size * 0.12);
  ctx.lineTo(size * 0.08, -size * 0.005);
  ctx.closePath();
  ctx.fillStyle = '#ecf2fa';
  ctx.fill();

  ctx.save();
  ctx.rotate((26 * Math.PI) / 180);
  ctx.fillStyle = '#6b4225';
  ctx.fillRect(size * 0.16, -size * 0.22, size * 0.034, size * 0.56);

  const crystalGradient = ctx.createRadialGradient(
    size * 0.18,
    -size * 0.28,
    size * 0.01,
    size * 0.18,
    -size * 0.28,
    size * 0.085
  );
  crystalGradient.addColorStop(0, '#f3fdff');
  crystalGradient.addColorStop(0.45, '#9ae9ff');
  crystalGradient.addColorStop(1, '#43b7ff');
  ctx.fillStyle = crystalGradient;
  ctx.beginPath();
  ctx.moveTo(size * 0.18, -size * 0.35);
  ctx.lineTo(size * 0.24, -size * 0.28);
  ctx.lineTo(size * 0.18, -size * 0.205);
  ctx.lineTo(size * 0.12, -size * 0.28);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = 'rgba(141, 230, 255, 0.8)';
  ctx.lineWidth = size * 0.013;
  ctx.beginPath();
  ctx.moveTo(size * 0.18, -size * 0.39);
  ctx.lineTo(size * 0.18, -size * 0.16);
  ctx.moveTo(size * 0.08, -size * 0.28);
  ctx.lineTo(size * 0.28, -size * 0.28);
  ctx.stroke();
  ctx.restore();

  drawBlobEyes(ctx, size, config);

  ctx.lineWidth = size * 0.018;
  ctx.strokeStyle = config.rimColor;
  ctx.beginPath();
  ctx.arc(0, size * 0.03, size * 0.3, Math.PI * 0.12, Math.PI * 0.88);
  ctx.stroke();

  ctx.restore();
}

export function drawBlobCrystalKnightProcedural(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  alpha: number,
  rotation: number = 0
): void {
  const config: BlobMobConfig = {
    bodyColor: '#5a6679',
    faceColor: '#efd1bc',
    shadowColor: 'rgba(58, 68, 89, 0.34)',
    rimColor: '#dfeaf7',
    accentColor: '#8fdcff',
    accentColorSecondary: '#7d8ea7',
    eyeColor: '#17202c',
    hatType: 'helmet',
    weaponType: 'none',
    accessoryType: 'none',
  };

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(centerX, centerY);
  ctx.rotate((rotation * Math.PI) / 180);

  ctx.shadowColor = config.shadowColor;
  ctx.shadowBlur = size * 0.17;
  drawBlobShadowRing(ctx, size, config.shadowColor);
  drawBlobBody(ctx, size, config);
  ctx.shadowBlur = 0;

  ctx.beginPath();
  ctx.moveTo(-size * 0.23, -size * 0.03);
  ctx.quadraticCurveTo(-size * 0.15, -size * 0.28, 0, -size * 0.26);
  ctx.quadraticCurveTo(size * 0.17, -size * 0.24, size * 0.23, -size * 0.03);
  ctx.lineTo(size * 0.12, size * 0.12);
  ctx.lineTo(-size * 0.12, size * 0.12);
  ctx.closePath();
  ctx.fillStyle = '#8d9bb0';
  ctx.fill();

  ctx.strokeStyle = '#f5fbff';
  ctx.lineWidth = size * 0.014;
  for (const x of [-0.075, -0.025, 0.025, 0.075]) {
    ctx.beginPath();
    ctx.moveTo(size * x, -size * 0.005);
    ctx.lineTo(size * x, size * 0.088);
    ctx.stroke();
  }

  ctx.beginPath();
  ctx.moveTo(-size * 0.06, -size * 0.26);
  ctx.lineTo(0, -size * 0.35);
  ctx.lineTo(size * 0.06, -size * 0.26);
  ctx.closePath();
  ctx.fillStyle = config.accentColor;
  ctx.fill();

  ctx.save();
  ctx.rotate((34 * Math.PI) / 180);
  ctx.beginPath();
  ctx.moveTo(-size * 0.24, -size * 0.02);
  ctx.lineTo(-size * 0.12, -size * 0.02);
  ctx.lineTo(-size * 0.085, size * 0.12);
  ctx.lineTo(-size * 0.18, size * 0.2);
  ctx.lineTo(-size * 0.275, size * 0.12);
  ctx.closePath();
  ctx.fillStyle = '#7b8ea7';
  ctx.fill();
  ctx.strokeStyle = '#d7f4ff';
  ctx.lineWidth = size * 0.012;
  ctx.stroke();
  ctx.restore();

  ctx.save();
  ctx.rotate((42 * Math.PI) / 180);
  ctx.fillStyle = '#f5f9ff';
  ctx.fillRect(size * 0.12, -size * 0.18, size * 0.045, size * 0.37);
  ctx.beginPath();
  ctx.moveTo(size * 0.12, -size * 0.18);
  ctx.lineTo(size * 0.142, -size * 0.3);
  ctx.lineTo(size * 0.165, -size * 0.18);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#9d6b3c';
  ctx.fillRect(size * 0.09, size * 0.11, size * 0.11, size * 0.028);
  ctx.fillStyle = config.accentColor;
  ctx.fillRect(size * 0.118, size * 0.06, size * 0.01, size * 0.14);
  ctx.restore();

  drawBlobEyes(ctx, size, config);

  ctx.lineWidth = size * 0.018;
  ctx.strokeStyle = config.rimColor;
  ctx.beginPath();
  ctx.arc(0, size * 0.03, size * 0.3, Math.PI * 0.12, Math.PI * 0.88);
  ctx.stroke();

  ctx.restore();
}

export function drawBlobSlimeProcedural(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  alpha: number,
  rotation: number = 0
): void {
  drawBlobMobProcedural(ctx, centerX, centerY, size, alpha, rotation, {
    bodyColor: '#5a6b45',
    faceColor: '#91a86a',
    shadowColor: 'rgba(86, 110, 61, 0.34)',
    rimColor: '#8a6a46',
    accentColor: '#d7d2bf',
    accentColorSecondary: '#4a3026',
    eyeColor: '#24110d',
    hatType: 'none',
    weaponType: 'none',
    accessoryType: 'collar',
    bodyScale: 0.98,
    bodyYOffset: size * 0.03,
  });
}

export function drawBlobDruidProcedural(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  alpha: number,
  rotation: number = 0
): void {
  drawBlobMobProcedural(ctx, centerX, centerY, size, alpha, rotation, {
    bodyColor: '#5a6948',
    faceColor: '#baa284',
    shadowColor: 'rgba(79, 96, 59, 0.34)',
    rimColor: '#92754f',
    accentColor: '#9daf79',
    accentColorSecondary: '#55663d',
    eyeColor: '#24140e',
    hatType: 'hood',
    weaponType: 'potion',
    accessoryType: 'leaves',
  });
}
